import React, { useCallback, useState, useEffect } from 'react';
import { Typography, Box, useMediaQuery, useTheme, Button, CircularProgress } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { Query } from '@material-table/core';
import { MTable, RepealableName } from '../../../../../Components';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { StepProps } from './types';
import { useAptorApi } from '../../../../../Api';
import { ICustomerLaw } from '../CustomerLaws/CustomerLaws.List';
import { IGridQueryRequest, OrderDirection } from '../../../../../Api/AptorApi';
import { LawListFilter } from '../CustomerLaws/CustomerLaws.Add.Filter';
import { useFilter } from '../../../../../Components/ListFilter';
import { StepButtonBar } from './StepButtonBar';

const PRIMARY = '#c4122f';

interface AddStep4LawsTableProps extends StepProps {
  selectedLaws: number[];
  setSelectedLaws: (laws: number[]) => void;
  selectedLawsData?: ICustomerLaw[];
  setSelectedLawsData?: (lawsData: ICustomerLaw[]) => void;
}

const AddStep4LawsTable = ({
  onNext,
  onBack,
  onCancel,
  currentData,
  isFirstStep,
  selectedLaws,
  setSelectedLaws,
  selectedLawsData = [],
  setSelectedLawsData,
}: AddStep4LawsTableProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableRef = React.useRef<any>(null);
  const { api } = useAptorApi();
  const [filters, setFilters] = useFilter();
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(3);
  const handleDataFetch = useCallback(
    async (query: Query<ICustomerLaw>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      return await api.searchNoneCustomerLaws<ICustomerLaw>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'number',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
    },
    [api, filters, isFiltersInitialized],
  );

  // Check if filters have been initialized
  useEffect(() => {
    const hasFilters = Object.values(filters).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0 && value.some(v => v !== "");
      } else if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(v => v !== "" && v !== null);
      }
      return value !== "" && value !== null;
    });

    if (hasFilters) {
      setIsFiltersInitialized(true);
    }
  }, [filters]);

  // Refresh table when filters change (after initialization)
  useEffect(() => {
    if (isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, isFiltersInitialized]);

  const check = (unit: ICustomerLaw) => {
    let isSelected = selectedLaws.includes(unit.id);
    return {
      tooltip: formatMessage({
        id: isSelected ? 'component.mtable.unselectTooltip' : 'component.mtable.selectTooltip',
      }),
      icon: isSelected ? 'check_box' : 'check_box_outline_blank',
      onClick: () => {
        if (isSelected) {
          setSelectedLaws(selectedLaws.filter((x) => x !== unit.id));
          setSelectedLawsData?.(selectedLawsData.filter(l => l.id !== unit.id));
        } else {
          setSelectedLaws([...selectedLaws, unit.id]);
          setSelectedLawsData?.([...selectedLawsData, unit]);
        }
      },
    };
  };

  const handleNext = () => {
    if (selectedLaws.length > 0) {
      onNext({ selectedLaws, selectedLawsData });
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* <div style={{ padding: isMobile ? '16px' : '24px' }}>

        <Box style={{ marginBottom: '16px' }}>
          <LawListFilter setFilter={setFilters} />
        </Box>

        <Box style={{ marginBottom: '16px' }}>
          <MTable
            actionLabel={formatMessage({ id: 'law-portal.manage.law-list.table.actions' })}
            tableRef={tableRef}
            columns={[
              { title: formatMessage({ id: 'law-portal.manage.law-list.table.number' }), field: 'number' },
              {
                title: formatMessage({ id: 'law-portal.manage.law-list.table.law' }),
                field: 'name',
                render: (row) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
              },
              {
                title: formatMessage({ id: 'law-portal.manage.law-list.table.description' }),
                field: 'description',
                render: (data: ICustomerLaw) => <HtmlView value={data.description} />,
                sorting: false,
              },
              { title: formatMessage({ id: 'law-portal.manage.law-list.table.area' }), field: 'area' },
            ]}
            data={handleDataFetch}
            actions={[check]}
            components={{}}
          />
        </Box>

        <Typography variant="body2" style={{ fontWeight: 600, color: PRIMARY }}>
          {formatMessage({ 
            id: 'law-portal.manage.add-laws.selectedLaws', 
            defaultMessage: 'Selected: {number}' 
          }, { number: selectedLaws.length })}
        </Typography>
      </div> */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: '100%',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
          }}
        >


          <Box style={{ maxHeight: '500px', overflow: 'auto' }}>
            <LawListFilter setFilter={setFilters} />
            <MTable
              actionLabel={formatMessage({ id: 'law-portal.manage.law-list.table.add.law' })}
              tableRef={tableRef}
              columns={[
                { title: formatMessage({ id: 'law-portal.manage.law-list.table.number' }), field: 'number' },
                {
                  title: formatMessage({ id: 'law-portal.manage.law-list.table.law' }),
                  field: 'name',
                  render: (row) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
                },
                {
                  title: formatMessage({ id: 'law-portal.manage.law-list.table.description' }),
                  field: 'description',
                  render: (data: ICustomerLaw) => <HtmlView value={data.description} />,
                  sorting: false,
                },
                { title: formatMessage({ id: 'law-portal.manage.law-list.table.area' }), field: 'area' },
              ]}
              data={handleDataFetch}
              actions={[check]}
              options={{ actionsColumnIndex: 0 }}
              components={{}}
            />
          </Box>

        </div>
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleNext}
          nextLabel="form.next"
          nextDisabled={selectedLaws.length === 0}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />
      </Box>
    </div>
  );
};

export default AddStep4LawsTable;
